import ClickLogger from '@/components/common/ClickLogger';
import { TreatmentRequestLog } from '@/entities/diagnosis/libs';
import { BookingTimeSchedule, TreatmentRequest } from '@/models/diagnosis';
import { cn } from '@/utils/style';

interface TimePeriodSectionProps {
  title: string;
  schedules: BookingTimeSchedule[];
  treatmentRequest: TreatmentRequest;
  selectBookingTimeSlot: (startTimeSlotValue: number, endTimeSlotValue: number) => void;
  showWarning?: boolean;
  warningMessage?: string;
}

const TimePeriodSection = ({
  title,
  schedules,
  treatmentRequest,
  selectBookingTimeSlot,
  showWarning = false,
  warningMessage,
}: TimePeriodSectionProps) => {
  if (!schedules || schedules.length === 0) {
    return null;
  }

  return (
    <div className='flex flex-col gap-[8px]'>
      <p className='typography-ui-13sb text-gray-700'>{title}</p>
      {showWarning && warningMessage && (
        <div className='flex items-center rounded-[8px] border-[#D3E6FF] border-[1px] border-solid bg-[#EFF6FF] px-[16px] py-[12px]'>
          <p className='typography-ui-13m text-gray-500'>{warningMessage}</p>
        </div>
      )}
      <div className='flex flex-wrap gap-[8px]'>
        {schedules.map((time) => {
          const isSelected = time.isSelected(
            treatmentRequest.form.startTimeSlotValue,
            treatmentRequest.form.endTimeSlotValue
          );
          const isDisabled = !time.isBookable;

          return (
            <ClickLogger
              key={`time-${time.startTimeSlot.value}`}
              params={{
                eventName: TreatmentRequestLog.RequestClickBookingTime,
              }}
            >
              <button
                type='button'
                disabled={isDisabled}
                onClick={() => {
                  selectBookingTimeSlot(time.startTimeSlot.value, time.endTimeSlot.value);
                }}
                className={cn(
                  'typography-ui-15m flex h-[56px] w-[calc((100%-16px)/3)] flex-col items-center justify-center rounded-[8px] border border-solid transition-all duration-300 ease-out',
                  isSelected && ['border-primary-600 bg-primary-100 text-primary-700'],
                  !isSelected &&
                    !isDisabled && [
                      'border-gray-100 bg-white text-gray-700 active:scale-95 active:border-gray-30 active:bg-gray-30 active:text-gray-700',
                    ],
                  isDisabled && ['cursor-not-allowed border-gray-40 bg-gray-40 text-gray-300']
                )}
              >
                {time.startTimeSlot.fromDisplayTime}
                <span
                  className={cn(
                    'typography-ui-13m',
                    isSelected && 'text-primary-700',
                    !isSelected && !isDisabled && 'text-gray-400',
                    isDisabled && 'text-gray-300'
                  )}
                >
                  ~ {time.endTimeSlot.toDisplayTime}
                </span>
              </button>
            </ClickLogger>
          );
        })}
      </div>
    </div>
  );
};

export default TimePeriodSection;
